import React, { useEffect, useState, useCallback } from 'react';
import { View, RefreshControl, Alert, Text, Pressable } from 'react-native';
import { FAB, Button, Portal, Modal, ActivityIndicator, Dialog } from 'react-native-paper';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchDashboard } from '../redux/slices/dashboardSlice';
import { isAdminRole } from '../redux/slices/authSlice';
import StatCard from '../components/StatCard';
import EmptyState from '../components/EmptyState';
import {
  Badge, Card, FilterChip, IconCircleButton, PageHeader, Screen, SectionHeader, TextInput,
} from '../components/ui';
import { RADIUS, TYPOGRAPHY } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useLayout } from '../theme/useLayout';
import { depenseService } from '../services/depenseService';
import { dashboardService } from '../services/dashboardService';
import { Depense, CategorieDepense, DashboardData, TypeClient } from '../types';

type IconName = React.ComponentProps<typeof MaterialCommunityIcons>['name'];

const CATEGORIES: { value: CategorieDepense; label: string; icon: IconName }[] = [
  { value: 'ACHAT', label: 'Achat', icon: 'cart-outline' },
  { value: 'LOYER', label: 'Loyer', icon: 'home-city-outline' },
  { value: 'SALAIRE', label: 'Salaire', icon: 'account-cash-outline' },
  { value: 'AUTRE', label: 'Autre', icon: 'dots-horizontal-circle-outline' },
];

const TYPE_CLIENT_OPTIONS: { value: 'TOUS' | TypeClient; label: string; icon: IconName }[] = [
  { value: 'TOUS', label: 'Tous', icon: 'account-group-outline' },
  { value: 'AGENCE', label: 'Sociétés', icon: 'office-building-outline' },
  { value: 'PARTICULIER', label: 'Particuliers', icon: 'account-outline' },
];

type CalendarTarget = 'DEBUT' | 'FIN';

type CalendarDay = {
  iso: string;
  day: number;
  isCurrentMonth: boolean;
};

const WEEK_DAYS = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];

const pad2 = (n: number) => String(n).padStart(2, '0');

const toIsoDate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const isoToFrDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '';
  return `${d}/${m}/${y}`;
};

const formatFrDateInput = (raw: string) => {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const validateFrDate = (fr: string): { iso: string | null; error: string | null } => {
  if (!fr) return { iso: null, error: 'Date requise.' };
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(fr)) {
    return { iso: null, error: 'Format invalide (JJ/MM/AAAA).' };
  }

  const [dStr, mStr, yStr] = fr.split('/');
  const day = Number(dStr);
  const month = Number(mStr);
  const year = Number(yStr);

  if (month < 1 || month > 12) {
    return { iso: null, error: 'Mois invalide.' };
  }
  if (day < 1 || day > 31) {
    return { iso: null, error: 'Jour invalide.' };
  }

  const parsed = new Date(year, month - 1, day);
  const isValid = parsed.getFullYear() === year
    && parsed.getMonth() === month - 1
    && parsed.getDate() === day;

  if (!isValid) {
    return { iso: null, error: 'Date inexistante.' };
  }

  return { iso: `${yStr}-${mStr}-${dStr}`, error: null };
};

const buildCalendarDays = (cursor: Date): CalendarDay[] => {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const firstDay = new Date(year, month, 1);
  const startWeekday = (firstDay.getDay() + 6) % 7; // Monday-based week
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();

  const cells: CalendarDay[] = [];

  for (let i = startWeekday; i > 0; i -= 1) {
    const day = prevMonthDays - i + 1;
    const date = new Date(year, month - 1, day);
    cells.push({ iso: toIsoDate(date), day, isCurrentMonth: false });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(year, month, day);
    cells.push({ iso: toIsoDate(date), day, isCurrentMonth: true });
  }

  let nextDay = 1;
  while (cells.length % 7 !== 0 || cells.length < 42) {
    const date = new Date(year, month + 1, nextDay);
    cells.push({ iso: toIsoDate(date), day: nextDay, isCurrentMonth: false });
    nextDay += 1;
  }

  return cells;
};

type DepenseRowProps = {
  depense: Depense;
  first: boolean;
  onDelete: () => void;
};

/** One expense line inside the expenses card. */
function DepenseRow({ depense, first, onDelete }: DepenseRowProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const categorie = CATEGORIES.find((c) => c.value === depense.categorie);

  return (
    <View style={[styles.depenseRow, !first && styles.rowDivider]}>
      <View style={styles.depenseIcon}>
        <MaterialCommunityIcons name={categorie?.icon ?? 'cash'} size={20} color={colors.textPrimary} />
      </View>
      <View style={styles.depenseInfo}>
        <Text numberOfLines={2} style={styles.depenseLibelle}>{depense.libelle}</Text>
        <View style={styles.depenseMeta}>
          <Badge label={categorie?.label ?? depense.categorie} />
          <Text style={styles.depenseDate}>{new Date(depense.date).toLocaleDateString('fr-FR')}</Text>
        </View>
      </View>
      <View style={styles.depenseRight}>
        <Text style={styles.depenseMontant} numberOfLines={1}>
          {Number(depense.montant).toLocaleString('fr-FR')} DH
        </Text>
        <IconCircleButton
          icon="trash-can-outline"
          variant="ghost"
          color={colors.danger}
          onPress={onDelete}
          accessibilityLabel={`Supprimer la dépense ${depense.libelle}`}
        />
      </View>
    </View>
  );
}

export default function GestionFinanciere() {
  const dispatch = useAppDispatch();
  const { data, loading: dashLoading } = useAppSelector((s) => s.dashboard);
  const isAdmin = useAppSelector((s) => isAdminRole(s.auth.role));
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { isDesktop } = useLayout();

  const [depenses, setDepenses] = useState<Depense[]>([]);
  const [loadingD, setLoadingD] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [libelle, setLibelle] = useState('');
  const [montant, setMontant] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [categorie, setCategorie] = useState<CategorieDepense>('ACHAT');
  const [saving, setSaving] = useState(false);

  // Period filter
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [dateDebutError, setDateDebutError] = useState('');
  const [dateFinError, setDateFinError] = useState('');
  const [dateError, setDateError] = useState('');
  const [periodeData, setPeriodeData] = useState<DashboardData | null>(null);
  const [periodeActive, setPeriodeActive] = useState(false);
  const [loadingPeriode, setLoadingPeriode] = useState(false);
  const [typeClientFilter, setTypeClientFilter] = useState<'TOUS' | TypeClient>('TOUS');
  const [showCalendarDialog, setShowCalendarDialog] = useState(false);
  const [calendarTarget, setCalendarTarget] = useState<CalendarTarget>('DEBUT');
  const [calendarCursor, setCalendarCursor] = useState(new Date());

  const selectedTypeClient: TypeClient | undefined = typeClientFilter === 'TOUS' ? undefined : typeClientFilter;

  const loadDepenses = useCallback(async () => {
    setLoadingD(true);
    try {
      const result = await depenseService.getAll();
      setDepenses(result);
    } catch {
      // ignore
    } finally {
      setLoadingD(false);
    }
  }, []);

  const load = useCallback(() => {
    dispatch(fetchDashboard(selectedTypeClient));
    loadDepenses();
  }, [dispatch, loadDepenses, selectedTypeClient]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <Screen>
        <EmptyState icon="lock-outline" message="Accès réservé à l'administrateur." />
      </Screen>
    );
  }

  const handleDateDebutChange = (raw: string) => {
    const formatted = formatFrDateInput(raw);
    setDateDebut(formatted);
    setDateError('');
    if (formatted.length === 10) {
      setDateDebutError(validateFrDate(formatted).error ?? '');
      return;
    }
    setDateDebutError('');
  };

  const handleDateFinChange = (raw: string) => {
    const formatted = formatFrDateInput(raw);
    setDateFin(formatted);
    setDateError('');
    if (formatted.length === 10) {
      setDateFinError(validateFrDate(formatted).error ?? '');
      return;
    }
    setDateFinError('');
  };

  const openCalendar = (target: CalendarTarget) => {
    setCalendarTarget(target);
    const source = target === 'DEBUT' ? dateDebut : dateFin;
    const parsed = validateFrDate(source);
    if (parsed.iso) {
      const [y, m, d] = parsed.iso.split('-').map(Number);
      setCalendarCursor(new Date(y, m - 1, d));
    } else {
      setCalendarCursor(new Date());
    }
    setShowCalendarDialog(true);
  };

  const handleCalendarPick = (iso: string) => {
    const fr = isoToFrDate(iso);
    if (calendarTarget === 'DEBUT') {
      handleDateDebutChange(fr);
    } else {
      handleDateFinChange(fr);
    }
    setShowCalendarDialog(false);
  };

  const handleFiltrer = async () => {
    const debutValidation = validateFrDate(dateDebut);
    const finValidation = validateFrDate(dateFin);

    setDateDebutError(debutValidation.error ?? '');
    setDateFinError(finValidation.error ?? '');

    if (!debutValidation.iso || !finValidation.iso) {
      setDateError('Veuillez corriger les dates saisies.');
      return;
    }

    const debutIso = debutValidation.iso;
    const finIso = finValidation.iso;

    if (debutIso > finIso) {
      setDateError('La date de début doit être antérieure à la date de fin');
      return;
    }
    setDateError('');
    setLoadingPeriode(true);
    try {
      const [dash, deps] = await Promise.all([
        dashboardService.getByPeriode(debutIso, finIso, selectedTypeClient),
        depenseService.getByPeriode(debutIso, finIso),
      ]);
      setPeriodeData(dash);
      setDepenses(deps);
      setPeriodeActive(true);
    } catch {
      Alert.alert('Erreur', 'Impossible de charger les données pour cette période.');
    } finally {
      setLoadingPeriode(false);
    }
  };

  const handleReinitialiser = () => {
    setDateDebut('');
    setDateFin('');
    setDateDebutError('');
    setDateFinError('');
    setDateError('');
    setPeriodeData(null);
    setPeriodeActive(false);
    load();
  };

  const handleTypeClientFilterChange = async (value: string) => {
    const nextFilter = value as 'TOUS' | TypeClient;
    setTypeClientFilter(nextFilter);
    const nextTypeClient = nextFilter === 'TOUS' ? undefined : nextFilter;

    if (!periodeActive) {
      dispatch(fetchDashboard(nextTypeClient));
      return;
    }

    const debutIso = validateFrDate(dateDebut).iso;
    const finIso = validateFrDate(dateFin).iso;
    if (!debutIso || !finIso) {
      setDateError('Veuillez corriger les dates saisies.');
      return;
    }

    setLoadingPeriode(true);
    try {
      const [dash, deps] = await Promise.all([
        dashboardService.getByPeriode(debutIso, finIso, nextTypeClient),
        depenseService.getByPeriode(debutIso, finIso),
      ]);
      setPeriodeData(dash);
      setDepenses(deps);
    } catch {
      Alert.alert('Erreur', 'Impossible d\'appliquer le filtre type client.');
    } finally {
      setLoadingPeriode(false);
    }
  };

  const handleCreate = async () => {
    if (!libelle.trim() || !montant) return;
    setSaving(true);
    try {
      await depenseService.create({
        libelle: libelle.trim(),
        montant: parseFloat(montant),
        date,
        categorie,
      });
      setShowModal(false);
      setLibelle(''); setMontant('');
      load();
    } catch {
      Alert.alert('Erreur', 'Impossible d\'enregistrer la dépense.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id: number) => {
    Alert.alert('Confirmer', 'Supprimer cette dépense ?', [
      { text: 'Annuler', style: 'cancel' },
      {
        text: 'Supprimer', style: 'destructive',
        onPress: async () => {
          await depenseService.delete(id);
          if (periodeActive) {
            const debutIso = validateFrDate(dateDebut).iso!;
            const finIso = validateFrDate(dateFin).iso!;
            const deps = await depenseService.getByPeriode(debutIso, finIso);
            setDepenses(deps);
          } else {
            loadDepenses();
          }
        },
      },
    ]);
  };

  const kpiData = periodeActive ? periodeData : data;
  const calendarDays = buildCalendarDays(calendarCursor);
  const calendarTitle = calendarCursor
    .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    .replace(/^./, (c) => c.toUpperCase());
  const selectedCalendarIso = validateFrDate(calendarTarget === 'DEBUT' ? dateDebut : dateFin).iso;
  const todayIso = toIsoDate(new Date());
  const periodeHint = periodeActive ? `Du ${dateDebut} au ${dateFin}` : undefined;
  const depensesCount = `${depenses.length} dépense${depenses.length > 1 ? 's' : ''}${periodeActive ? ' sur la période' : ''}`;

  return (
    <View style={styles.container}>
      <Screen
        refreshControl={(
          <RefreshControl
            refreshing={dashLoading || loadingD || loadingPeriode}
            onRefresh={load}
            colors={[colors.primary]}
            tintColor={colors.primary}
          />
        )}
        contentStyle={!isDesktop ? styles.contentWithFab : undefined}
      >
        <PageHeader
          title="Gestion financière"
          subtitle="Chiffre d'affaires, encaissements et dépenses de l'activité."
          actions={isDesktop ? (
            <Button mode="contained" icon="plus" onPress={() => setShowModal(true)}>
              Nouvelle dépense
            </Button>
          ) : undefined}
        />

        {/* Filters */}
        <Card style={styles.filterCard}>
          <View style={[styles.filterGrid, isDesktop && styles.filterGridDesktop]}>
            <View style={[styles.filterBlock, isDesktop && styles.filterBlockPeriod]}>
              <Text style={styles.filterLabel}>Période</Text>
              <View style={styles.dateRow}>
                <View style={styles.dateFieldWrap}>
                  <TextInput
                    label="Date début"
                    value={dateDebut}
                    onChangeText={handleDateDebutChange}
                    placeholder="JJ/MM/AAAA"
                    dense
                    keyboardType="numeric"
                    maxLength={10}
                    error={!!dateDebutError}
                    right={<TextInput.Icon icon="calendar-month-outline" onPress={() => openCalendar('DEBUT')} />}
                  />
                  {!!dateDebutError && <Text style={styles.fieldError}>{dateDebutError}</Text>}
                </View>
                <View style={styles.dateFieldWrap}>
                  <TextInput
                    label="Date fin"
                    value={dateFin}
                    onChangeText={handleDateFinChange}
                    placeholder="JJ/MM/AAAA"
                    dense
                    keyboardType="numeric"
                    maxLength={10}
                    error={!!dateFinError}
                    right={<TextInput.Icon icon="calendar-month-outline" onPress={() => openCalendar('FIN')} />}
                  />
                  {!!dateFinError && <Text style={styles.fieldError}>{dateFinError}</Text>}
                </View>
              </View>
              {!!dateError && (
                <View style={styles.formError}>
                  <MaterialCommunityIcons name="alert-circle-outline" size={16} color={colors.danger} />
                  <Text style={styles.formErrorText}>{dateError}</Text>
                </View>
              )}
              <View style={styles.filterActions}>
                <Button
                  mode="contained"
                  buttonColor={colors.ink}
                  textColor={colors.onInk}
                  icon="filter-variant"
                  onPress={handleFiltrer}
                  loading={loadingPeriode}
                  disabled={loadingPeriode}
                >
                  Filtrer
                </Button>
                {periodeActive && (
                  <Button mode="outlined" textColor={colors.textPrimary} onPress={handleReinitialiser}>
                    Réinitialiser
                  </Button>
                )}
              </View>
            </View>

            <View style={[styles.filterBlock, isDesktop && styles.filterBlockType]}>
              <Text style={styles.filterLabel}>Type de client</Text>
              <View style={styles.chipRow}>
                {TYPE_CLIENT_OPTIONS.map((o) => (
                  <FilterChip
                    key={o.value}
                    label={o.label}
                    icon={o.icon}
                    selected={typeClientFilter === o.value}
                    onPress={() => handleTypeClientFilterChange(o.value)}
                  />
                ))}
              </View>
            </View>
          </View>

          {periodeActive && (
            <Badge
              tone="brand"
              icon="calendar-range"
              label={`Période : ${dateDebut} → ${dateFin}`}
              style={styles.periodBadge}
            />
          )}
        </Card>

        {/* KPI Summary */}
        <SectionHeader title="Indicateurs clés" />
        <View style={styles.kpiRow}>
          <StatCard
            label="Chiffre d'affaires total"
            value={`${(kpiData?.chiffreAffairesTotal ?? 0).toLocaleString('fr-FR')} DH`}
            tone="primary"
            icon="chart-line"
            hint={periodeHint}
          />
          <StatCard
            label="Total avances reçues"
            value={`${(kpiData?.totalAvances ?? 0).toLocaleString('fr-FR')} DH`}
            tone="brand"
            icon="cash-check"
            hint={periodeHint}
          />
          <StatCard
            label="Reste à recouvrer"
            value={`${(kpiData?.totalResteARecouvrer ?? 0).toLocaleString('fr-FR')} DH`}
            tone="danger"
            icon="clock-alert-outline"
            hint={periodeHint}
          />
          <StatCard
            label="Total dépenses"
            value={`${(kpiData?.totalDepenses ?? 0).toLocaleString('fr-FR')} DH`}
            tone="warning"
            icon="cash-minus"
            hint={periodeHint}
          />
        </View>

        {/* Depenses */}
        <SectionHeader title="Dépenses" subtitle={depensesCount} />
        {loadingD ? (
          <ActivityIndicator color={colors.primary} style={styles.loader} />
        ) : depenses.length === 0 ? (
          <Card>
            <EmptyState icon="receipt" message="Aucune dépense enregistrée" />
          </Card>
        ) : (
          <Card flush>
            {depenses.map((d, index) => (
              <DepenseRow key={d.id} depense={d} first={index === 0} onDelete={() => handleDelete(d.id)} />
            ))}
          </Card>
        )}
      </Screen>

      {!isDesktop && (
        <FAB
          icon="plus"
          label="Dépense"
          style={styles.fab}
          color={colors.onPrimary}
          onPress={() => setShowModal(true)}
        />
      )}

      <Portal>
        <Dialog visible={showCalendarDialog} onDismiss={() => setShowCalendarDialog(false)} style={styles.calendarDialog}>
          <Dialog.Title style={styles.dialogTitle}>
            {calendarTarget === 'DEBUT' ? 'Choisir la date de début' : 'Choisir la date de fin'}
          </Dialog.Title>
          <Dialog.Content>
            <View style={styles.calendarHeader}>
              <IconCircleButton
                icon="chevron-left"
                size={36}
                accessibilityLabel="Mois précédent"
                onPress={() => setCalendarCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
              />
              <Text style={styles.calendarTitle}>{calendarTitle}</Text>
              <IconCircleButton
                icon="chevron-right"
                size={36}
                accessibilityLabel="Mois suivant"
                onPress={() => setCalendarCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
              />
            </View>

            <View style={styles.calendarWeekRow}>
              {WEEK_DAYS.map((wd) => (
                <Text key={wd} style={styles.calendarWeekDay}>{wd}</Text>
              ))}
            </View>

            <View style={styles.calendarGrid}>
              {calendarDays.map((cell) => {
                const isSelected = cell.iso === selectedCalendarIso;
                const isToday = cell.iso === todayIso;
                return (
                  <Pressable
                    key={cell.iso}
                    style={styles.calendarCell}
                    onPress={() => handleCalendarPick(cell.iso)}
                    accessibilityRole="button"
                    accessibilityLabel={isoToFrDate(cell.iso)}
                    accessibilityState={{ selected: isSelected }}
                  >
                    {(state) => {
                      const hovered = (state as { hovered?: boolean }).hovered || state.pressed;
                      return (
                        <View
                          style={[
                            styles.calendarDay,
                            isToday && !isSelected && styles.calendarDayToday,
                            hovered && !isSelected && styles.calendarDayHovered,
                            isSelected && styles.calendarDaySelected,
                          ]}
                        >
                          <Text
                            style={[
                              styles.calendarDayText,
                              !cell.isCurrentMonth && styles.calendarDayTextMuted,
                              isToday && styles.calendarDayTextStrong,
                              isSelected && styles.calendarDayTextSelected,
                            ]}
                          >
                            {cell.day}
                          </Text>
                        </View>
                      );
                    }}
                  </Pressable>
                );
              })}
            </View>
          </Dialog.Content>
          <Dialog.Actions>
            <Button textColor={colors.textPrimary} onPress={() => setShowCalendarDialog(false)}>Fermer</Button>
          </Dialog.Actions>
        </Dialog>

        <Modal visible={showModal} onDismiss={() => setShowModal(false)} contentContainerStyle={styles.modal}>
          <Text style={styles.modalTitle}>Nouvelle dépense</Text>
          <TextInput label="Libellé *" value={libelle} onChangeText={setLibelle} style={styles.input} />
          <TextInput
            label="Montant (DH) *"
            value={montant}
            onChangeText={setMontant}
            keyboardType="decimal-pad"
            style={styles.input}
          />
          <TextInput
            label="Date"
            value={date}
            onChangeText={setDate}
            style={styles.input}
            placeholder="AAAA-MM-JJ"
          />
          <Text style={styles.fieldLabel}>Catégorie</Text>
          <View style={[styles.chipRow, styles.categoryRow]}>
            {CATEGORIES.map((c) => (
              <FilterChip
                key={c.value}
                label={c.label}
                icon={c.icon}
                selected={categorie === c.value}
                onPress={() => setCategorie(c.value)}
              />
            ))}
          </View>
          <View style={styles.modalActions}>
            <Button textColor={colors.textPrimary} onPress={() => setShowModal(false)}>Annuler</Button>
            <Button mode="contained" onPress={handleCreate} loading={saving} disabled={saving || !libelle || !montant}>
              Enregistrer
            </Button>
          </View>
        </Modal>
      </Portal>
    </View>
  );
}

const useStyles = makeStyles((c) => ({
  container: { flex: 1, backgroundColor: c.background },
  contentWithFab: { paddingBottom: 40 },
  loader: { marginVertical: 32 },

  // Filters
  filterCard: { marginBottom: 32 },
  filterGrid: { gap: 20 },
  filterGridDesktop: { flexDirection: 'row', alignItems: 'flex-start', gap: 32 },
  filterBlock: { gap: 12 },
  filterBlockPeriod: { flex: 3 },
  filterBlockType: { flex: 2 },
  filterLabel: { ...TYPOGRAPHY.label, color: c.textSecondary },
  dateRow: { flexDirection: 'row', gap: 12 },
  dateFieldWrap: { flex: 1, minWidth: 0 },
  fieldError: { ...TYPOGRAPHY.caption, color: c.danger, marginTop: 4, marginLeft: 4 },
  formError: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  formErrorText: { fontSize: 13, color: c.danger, flexShrink: 1 },
  filterActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  periodBadge: { marginTop: 16 },

  // KPI
  kpiRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginBottom: 32 },

  // Expenses
  depenseRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingVertical: 16,
    paddingHorizontal: 20,
  },
  rowDivider: { borderTopWidth: 1, borderTopColor: c.divider },
  depenseIcon: {
    width: 40,
    height: 40,
    borderRadius: RADIUS.md,
    backgroundColor: c.surfaceAlt,
    alignItems: 'center',
    justifyContent: 'center',
  },
  depenseInfo: { flex: 1, minWidth: 0 },
  depenseLibelle: { ...TYPOGRAPHY.bodyStrong, color: c.textPrimary, flexShrink: 1 },
  depenseMeta: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 6 },
  depenseDate: { fontSize: 13, color: c.textSecondary },
  depenseRight: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  depenseMontant: { fontSize: 15, fontWeight: '700', color: c.textPrimary },

  fab: { position: 'absolute', bottom: 16, right: 16, backgroundColor: c.primary },

  // Calendar dialog
  calendarDialog: {
    alignSelf: 'center',
    width: 380,
    maxWidth: '94%',
    backgroundColor: c.surface,
    borderRadius: RADIUS.xl,
  },
  dialogTitle: { ...TYPOGRAPHY.heading, color: c.textPrimary },
  calendarHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  calendarTitle: { ...TYPOGRAPHY.subheading, color: c.textPrimary },
  calendarWeekRow: { flexDirection: 'row', marginBottom: 4 },
  calendarWeekDay: {
    width: '14.285%',
    textAlign: 'center',
    ...TYPOGRAPHY.caption,
    color: c.textSecondary,
  },
  calendarGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  calendarCell: { width: '14.285%', height: 42, alignItems: 'center', justifyContent: 'center' },
  calendarDay: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'transparent',
  },
  calendarDayToday: { borderColor: c.border },
  calendarDayHovered: { borderColor: c.ink },
  calendarDaySelected: { backgroundColor: c.ink, borderColor: c.ink },
  calendarDayText: { fontSize: 14, fontWeight: '500', color: c.textPrimary },
  calendarDayTextMuted: { color: c.textMuted },
  calendarDayTextStrong: { fontWeight: '800' },
  calendarDayTextSelected: { color: c.onInk, fontWeight: '700' },

  // Create modal
  modal: {
    backgroundColor: c.surface,
    borderRadius: RADIUS.xl,
    padding: 24,
    margin: 16,
    maxWidth: 560,
    width: '100%',
    alignSelf: 'center',
  },
  modalTitle: { ...TYPOGRAPHY.title, color: c.textPrimary, marginBottom: 20 },
  input: { marginBottom: 12 },
  fieldLabel: { ...TYPOGRAPHY.label, color: c.textSecondary, marginTop: 4, marginBottom: 10 },
  categoryRow: { marginBottom: 8 },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
}));
